import { maybe } from '@passionware/monads';
import { QueryClient } from '@tanstack/react-query';
import {
  isSettingName,
  settingCatalog,
  SettingName
} from 'v5/api/system-settings/settingCatalog';
import {
  SystemSettingsApi,
  SystemSetting
} from 'v5/api/system-settings/system-settings.api';
import {
  AppScopeIdentity,
  appScopeUtils
} from 'v5/core/app-scope/appScope.types';
import { assertWriteScope, resolveSettingValue } from './resolveSystemSettings';
import { SystemSettingsMutationService } from './SystemSettingsMutationService';
import { systemSettingsQueryKey } from './SystemSettingsService.impl';

function sameExactScope(a: SystemSetting, b: SystemSetting): boolean {
  return (
    a.name === b.name &&
    (a.enterprise_workspace_id ?? null) ===
      (b.enterprise_workspace_id ?? null) &&
    (a.enterprise_team_id ?? null) === (b.enterprise_team_id ?? null)
  );
}

/** Replace the matching name+scope row (or append) in the org settings cache. */
function replaceCachedSettingRow(
  queryClient: QueryClient,
  organizationId: string,
  row: SystemSetting
): SystemSetting[] {
  const key = systemSettingsQueryKey(organizationId);
  const previous = queryClient.getQueryData<SystemSetting[]>(key) ?? [];
  const next = [
    ...previous.filter(existing => !sameExactScope(existing, row)),
    row
  ];
  queryClient.setQueryData(key, next);
  return next;
}

export function createSystemSettingsMutationService(config: {
  api: SystemSettingsApi;
  queryClient: QueryClient;
}): SystemSettingsMutationService {
  const { api, queryClient } = config;

  async function updateSetting(
    scope: AppScopeIdentity,
    name: SettingName,
    value: unknown
  ) {
    assertWriteScope(scope, name);
    const definedAt = settingCatalog[name].definedAt;
    const enterpriseWorkspaceId = (
      scope as Extract<AppScopeIdentity, { level: 'workspace' | 'team' }>
    ).workspaceId;
    const enterpriseTeamId =
      definedAt === 'team' && scope.level === 'team' ? scope.teamId : null;

    const row = await api.upsertSetting({
      organizationId: scope.organizationId,
      name,
      setting: value,
      enterpriseWorkspaceId,
      enterpriseTeamId
    });

    const rows = replaceCachedSettingRow(
      queryClient,
      scope.organizationId,
      row
    );
    return resolveSettingValue(rows, scope, name);
  }

  return {
    updateSetting,
    async updateSettings(
      scope: AppScopeIdentity,
      patch: Record<string, unknown>
    ) {
      const names = Object.keys(patch).filter(isSettingName);
      names.forEach(name => assertWriteScope(scope, name));
      if (names.length === 0) return;

      const rows = await api.upsertSettings({
        organizationId: scope.organizationId,
        settings: Object.fromEntries(names.map(name => [name, patch[name]])),
        enterpriseWorkspaceId: maybe.getOrThrow(
          appScopeUtils.getWorkspaceId(scope),
          'Settings are written at a workspace or team scope'
        ),
        enterpriseTeamId: scope.level === 'team' ? scope.teamId : null
      });
      rows.forEach(row =>
        replaceCachedSettingRow(queryClient, scope.organizationId, row)
      );
    }
  };
}
